import React, { useState, useEffect } from "react";
import { Card, Table, Button, Space, Typography, Empty, Spin, Popconfirm, Tabs } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  EditOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { API_URL, getCredentials } from "../../shared/utils/Util";
import { getFetchT, centsToDollar } from "../../shared/utils/TypedUtil";
import { useAntdTheme, useNotification } from "../../shared/hooks";
import { LargeVolumeDeliveryZone, LargeVolumeDeliveryPostalCode } from "./types";
import { ZoneForm } from "./ZoneForm";
import { PostalCodeForm } from "./PostalCodeForm";

const { Text } = Typography;

type LargeVolumeDeliveryEditorProps = {
  restaurantId: string;
};

export const LargeVolumeDeliveryEditor: React.FC<LargeVolumeDeliveryEditorProps> = ({
  restaurantId,
}) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const { deleteSuccess, notifyError } = useNotification();
  const [zones, setZones] = useState<LargeVolumeDeliveryZone[] | undefined>(undefined);
  const [postalCodes, setPostalCodes] = useState<LargeVolumeDeliveryPostalCode[] | undefined>(
    undefined
  );
  const [deletingZone, setDeletingZone] = useState<string | null>(null);
  const [deletingPostalCode, setDeletingPostalCode] = useState<string | null>(null);
  const [editingZone, setEditingZone] = useState<LargeVolumeDeliveryZone | null>(null);
  const [editingPostalCode, setEditingPostalCode] = useState<LargeVolumeDeliveryPostalCode | null>(
    null
  );

  const refreshZones = () => {
    getFetchT<{ large_volume_delivery_zones: LargeVolumeDeliveryZone[] }>(
      `${API_URL}/v1/r/${restaurantId}/admin/large_volume_delivery_zones`,
      (result) => setZones(result.large_volume_delivery_zones || []),
      (err) => {
        notifyError(t("failed_to_fetch_delivery_zones"), err);
        setZones([]);
      }
    );
  };

  const refreshPostalCodes = () => {
    getFetchT<{ large_volume_delivery_postal_codes: LargeVolumeDeliveryPostalCode[] }>(
      `${API_URL}/v1/r/${restaurantId}/admin/large_volume_delivery_postal_codes`,
      (result) => setPostalCodes(result.large_volume_delivery_postal_codes || []),
      (err) => {
        notifyError(t("failed_to_fetch_postal_codes"), err);
        setPostalCodes([]);
      }
    );
  };

  useEffect(() => {
    refreshZones();
    refreshPostalCodes();
  }, [restaurantId]);

  const handleDeleteZone = (zoneId: string) => {
    setDeletingZone(zoneId);
    fetch(`${API_URL}/v1/r/${restaurantId}/admin/large_volume_delivery_zones/${zoneId}`, {
      credentials: "include",
      method: "DELETE",
      headers: {
        Authorization: `Basic ${getCredentials()}`,
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw res;
        }
        deleteSuccess(t("delivery_zone"));
        setZones(undefined);
        refreshZones();
      })
      .catch((err) => {
        err.text().then((errorMessage: string) => {
          notifyError(t("failed_to_delete_delivery_zone"), errorMessage);
        });
      })
      .finally(() => {
        setDeletingZone(null);
      });
  };

  const handleDeletePostalCode = (postalCode: string) => {
    setDeletingPostalCode(postalCode);
    fetch(
      `${API_URL}/v1/r/${restaurantId}/admin/large_volume_delivery_postal_codes/${postalCode}`,
      {
        credentials: "include",
        method: "DELETE",
        headers: {
          Authorization: `Basic ${getCredentials()}`,
        },
      }
    )
      .then((res) => {
        if (!res.ok) {
          throw res;
        }
        deleteSuccess(t("postal_code"));
        setPostalCodes(undefined);
        refreshPostalCodes();
      })
      .catch((err) => {
        err.text().then((errorMessage: string) => {
          notifyError(t("failed_to_delete_postal_code"), errorMessage);
        });
      })
      .finally(() => {
        setDeletingPostalCode(null);
      });
  };

  const handleEditZone = (zone: LargeVolumeDeliveryZone) => {
    setEditingZone(zone);
  };

  const handleCancelEditZone = () => {
    setEditingZone(null);
  };

  const handleEditPostalCode = (postalCode: LargeVolumeDeliveryPostalCode) => {
    setEditingPostalCode(postalCode);
  };

  const handleCancelEditPostalCode = () => {
    setEditingPostalCode(null);
  };

  const onZoneChange = () => {
    setZones(undefined);
    setEditingZone(null);
    refreshZones();
  };

  const onPostalCodeChange = () => {
    setPostalCodes(undefined);
    setEditingPostalCode(null);
    refreshPostalCodes();
  };

  const getZoneName = (zoneId: string): string => {
    const zone = zones?.find((z) => z.id === zoneId);
    return zone?.name || zoneId;
  };

  if (zones === undefined || postalCodes === undefined) {
    return <Spin size="large" />;
  }

  const zoneColumns = [
    {
      title: t("name"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("minimum_pretax_price"),
      dataIndex: "minimum_pretax_price",
      key: "minimum_pretax_price",
      render: (value: number) => `$${centsToDollar(value)}`,
    },
    {
      title: t("delivery_fee"),
      dataIndex: "delivery_fee",
      key: "delivery_fee",
      render: (value: number) => `$${centsToDollar(value)}`,
    },
    {
      title: t("free_delivery_threshold"),
      dataIndex: "free_delivery_threshold",
      key: "free_delivery_threshold",
      render: (value: number) => `$${centsToDollar(value)}`,
    },
    {
      title: t("actions"),
      key: "actions",
      render: (_: unknown, record: LargeVolumeDeliveryZone) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEditZone(record)}>
            {t("edit")}
          </Button>
          <Popconfirm
            title={t("delete_delivery_zone_confirm")}
            description={t("delete_delivery_zone_description")}
            onConfirm={() => handleDeleteZone(record.id)}
            okText={t("yes")}
            cancelText={t("no")}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              loading={deletingZone === record.id}
            >
              {t("delete")}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const postalCodeColumns = [
    {
      title: t("postal_code"),
      dataIndex: "postal_code",
      key: "postal_code",
    },
    {
      title: t("delivery_zone"),
      dataIndex: "zone_id",
      key: "zone_id",
      render: (value: string) => getZoneName(value),
    },
    {
      title: t("actions"),
      key: "actions",
      render: (_: unknown, record: LargeVolumeDeliveryPostalCode) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEditPostalCode(record)}>
            {t("edit")}
          </Button>
          <Popconfirm
            title={t("delete_postal_code_confirm")}
            description={t("delete_postal_code_description")}
            onConfirm={() => handleDeletePostalCode(record.postal_code)}
            okText={t("yes")}
            cancelText={t("no")}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              loading={deletingPostalCode === record.postal_code}
            >
              {t("delete")}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const zonesTab = (
    <div>
      <Card
        title={
          <Space>
            <PlusOutlined style={{ fontSize: 20, color: token.colorSuccess }} />
            <Text strong style={{ fontSize: token.fontSizeLG }}>
              {editingZone ? t("edit_delivery_zone") : t("add_delivery_zone")}
            </Text>
          </Space>
        }
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary,
          border: `1px solid ${token.colorBorder}`,
          marginBottom: token.marginXL,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        <ZoneForm
          restaurantId={restaurantId}
          onSuccess={onZoneChange}
          editingZone={editingZone}
          onCancelEdit={handleCancelEditZone}
        />
      </Card>

      <Card
        title={
          <Space>
            <EnvironmentOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
            <Text strong style={{ fontSize: token.fontSizeLG }}>
              {t("current_delivery_zones")}
            </Text>
          </Space>
        }
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary,
          border: `1px solid ${token.colorBorder}`,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        {zones.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<Text type="secondary">{t("no_delivery_zones_available")}</Text>}
          />
        ) : (
          <Table
            columns={zoneColumns}
            dataSource={zones}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `${t("total")} ${total} ${t("delivery_zones")}`,
            }}
            style={{ borderRadius: token.borderRadiusLG }}
          />
        )}
      </Card>
    </div>
  );

  const postalCodesTab = (
    <div>
      <Card
        title={
          <Space>
            <PlusOutlined style={{ fontSize: 20, color: token.colorSuccess }} />
            <Text strong style={{ fontSize: token.fontSizeLG }}>
              {editingPostalCode ? t("edit_postal_code") : t("add_postal_code")}
            </Text>
          </Space>
        }
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary,
          border: `1px solid ${token.colorBorder}`,
          marginBottom: token.marginXL,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        {zones.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<Text type="secondary">{t("create_delivery_zone_first")}</Text>}
          />
        ) : (
          <PostalCodeForm
            restaurantId={restaurantId}
            zones={zones}
            onSuccess={onPostalCodeChange}
            editingPostalCode={editingPostalCode}
            onCancelEdit={handleCancelEditPostalCode}
          />
        )}
      </Card>

      <Card
        title={
          <Space>
            <MailOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
            <Text strong style={{ fontSize: token.fontSizeLG }}>
              {t("current_postal_codes")}
            </Text>
          </Space>
        }
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary,
          border: `1px solid ${token.colorBorder}`,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        {postalCodes.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<Text type="secondary">{t("no_postal_codes_available")}</Text>}
          />
        ) : (
          <Table
            columns={postalCodeColumns}
            dataSource={postalCodes}
            rowKey="postal_code"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `${t("total")} ${total} ${t("postal_codes")}`,
            }}
            style={{ borderRadius: token.borderRadiusLG }}
          />
        )}
      </Card>
    </div>
  );

  const tabItems = [
    {
      key: "zones",
      label: (
        <Space>
          <EnvironmentOutlined />
          {t("delivery_zones")}
        </Space>
      ),
      children: zonesTab,
    },
    {
      key: "postal_codes",
      label: (
        <Space>
          <MailOutlined />
          {t("postal_codes")}
        </Space>
      ),
      children: postalCodesTab,
    },
  ];

  return <Tabs items={tabItems} />;
};
